import React from 'react';
import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import PromoCodeField from '../PromoCodeField';
import SavingsSummary from '../SavingsSummary';
import CartVehicleCard from '../CartVehicleCard';
import { CartFitWarning } from '../CartFitBadge';
import { useT } from '../../services/i18n';

const CheckoutSummary = ({ cart, promo, setPromo, isProcessing, orderComplete, totals, selectedShip, isPro, vatRate, savings, wallet, useWallet, setUseWallet, shippingInfo }) => {
  const { t } = useT();
  const { subtotal, promoDiscount, shipping, fittingTotal, total, walletUsed, toPay } = totals;
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 sticky top-4">
      <h2 className="text-lg font-bold text-gray-900 mb-4">{t('checkout.summary')}</h2>
      <CartVehicleCard testId="checkout-vehicle" compact />
      <CartFitWarning items={cart.items} testId="checkout-fit-warning" />

      <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
        {cart.items.map(item => (
          <div key={`${item.product_id}-${item.pack_id || 'u'}`} className="flex gap-3">
            <Link to={`/produit/${item.product_id}`} className="w-16 h-16 bg-gray-100 rounded flex-shrink-0 block">
              <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate"><Link to={`/produit/${item.product_id}`} className="hover:text-orange-600 hover:underline">{item.name}</Link></p>
              <p className="text-xs text-gray-500">{item.pack_label ? `${item.pack_label} · ` : ''}{t('checkout.qty', { n: item.quantity })}</p>
              <p className="text-sm font-bold text-gray-900">{(item.price * item.quantity).toFixed(2)} €</p>
            </div>
          </div>
        ))}
      </div>

      <hr className="my-4" />

      {!orderComplete && <div className="mb-4"><PromoCodeField promo={promo} onChange={setPromo} disabled={isProcessing} /></div>}

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('common.subtotal')}</span>
          <span className="font-medium">{subtotal.toFixed(2)} €</span>
        </div>
        {promo && (
          <div className="flex justify-between text-green-700" data-testid="checkout-promo-line">
            <span>{t('common.discount', { code: promo.code })} </span>
            <span className="font-medium">-{promoDiscount.toFixed(2)} €</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">{t('common.shipping')}{selectedShip ? <span className="text-xs text-gray-400"> · {selectedShip.name}</span> : null}</span>
          <span className={`font-medium ${shipping === 0 ? 'text-green-600' : ''}`} data-testid="checkout-shipping-amount">{shipping === 0 ? t('common.free') : `${shipping.toFixed(2)} €`}</span>
        </div>
        {fittingTotal > 0 && (
          <div className="flex justify-between" data-testid="checkout-fitting-line">
            <span className="text-gray-600">{t('checkout.fitting')}</span>
            <span className="font-medium">{fittingTotal.toFixed(2)} €</span>
          </div>
        )}
        <hr />
        {isPro && (
          <div className="flex justify-between text-xs text-gray-500" data-testid="checkout-ht-line">
            <span>{t('checkout.totalHT')}</span>
            <span>{(total / (1 + vatRate / 100)).toFixed(2)} € <span className="text-gray-400">{t('checkout.vatAmount', { amount: (total - total / (1 + vatRate / 100)).toFixed(2) })}</span></span>
          </div>
        )}
        <div className="flex justify-between text-lg font-bold">
          <span>{t('checkout.totalTTC')}</span>
          <span className="text-orange-500">{total.toFixed(2)} €</span>
        </div>
        {!orderComplete && <SavingsSummary savings={savings} testId="checkout-savings" />}
        {wallet > 0 && !orderComplete && (
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="checkout-wallet">
            <label className="flex items-center gap-2 text-sm cursor-pointer text-green-900">
              <input type="checkbox" data-testid="checkout-use-wallet" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="rounded border-gray-300 text-green-600" />
              <Wallet size={15} /> {t('checkout.useWallet', { amount: wallet.toFixed(2) })}
            </label>
            {useWallet && (
              <div className="mt-2 text-sm space-y-1">
                <div className="flex justify-between text-green-800"><span>{t('checkout.walletUsed')}</span><span data-testid="checkout-wallet-used">−{walletUsed.toFixed(2)} €</span></div>
                <div className="flex justify-between font-bold text-gray-900"><span>{t('checkout.toPay')}</span><span data-testid="checkout-to-pay">{toPay.toFixed(2)} €</span></div>
              </div>
            )}
          </div>
        )}
      </div>

      {shippingInfo.address && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-1">{t('checkout.shippingAddress')}</p>
          <p className="text-sm text-gray-900">
            {shippingInfo.firstName} {shippingInfo.lastName}<br />
            {shippingInfo.address}<br />
            {shippingInfo.postalCode} {shippingInfo.city}
          </p>
        </div>
      )}
    </div>
  );
};

export default CheckoutSummary;
